import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
    HomeIcon,
    AcademicCapIcon,
    BookOpenIcon,
    UserGroupIcon,
    PhotoIcon,
    QuestionMarkCircleIcon,
    Bars3Icon,
    XMarkIcon,
    ArrowRightOnRectangleIcon,
    UserIcon,
    CheckBadgeIcon,
    CogIcon,
    DocumentTextIcon,
    PencilSquareIcon,
    ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../hooks/useAuth'

const AdminLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const { user, logout } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()

    // Helper function to check if user has permission for a section
    const hasPermission = (section, action = 'read') => {
        if (!user || !user.permissions) return false
        if (user.role === 'admin') return true // Admin has all permissions
        return user.permissions[section] && user.permissions[section][action]
    }

    // Helper function to get user role display name
    const getUserRoleDisplay = () => {
        if (!user) return 'Admin'
        switch (user.role) {
            case 'admin': return 'Administrator'
            case 'editor': return 'Editor'
            case 'viewer': return 'Viewer'
            default: return 'User'
        }
    }

    // Helper function to get user department or role
    const getUserDepartment = () => {
        if (!user) return 'MyMargdarshak'
        return user.department || getUserRoleDisplay()
    }

    // Helper function to get user access summary
    const getUserAccessSummary = () => {
        if (!user || user.role === 'admin') return 'Full Access'

        const accessSections = []
        if (hasPermission('blogs')) accessSections.push('Blogs')
        if (hasPermission('faqs')) accessSections.push('FAQs')
        if (hasPermission('courses')) accessSections.push('Courses')
        if (hasPermission('universities')) accessSections.push('Universities')
        if (hasPermission('enquiries')) accessSections.push('Enquiries')
        if (hasPermission('banners')) accessSections.push('Banners')
        if (hasPermission('settings')) accessSections.push('Settings')

        return accessSections.length > 0 ? accessSections.join(', ') : 'Limited Access'
    }

    // Filter navigation based on user permissions
    const getFilteredNavigation = () => {
        const allNavigation = [
            { name: 'Dashboard', href: '/admin/dashboard', icon: HomeIcon, permission: null }, // Dashboard always visible
            { name: 'Universities', href: '/admin/universities', icon: AcademicCapIcon, permission: 'universities' },
            { name: 'Courses', href: '/admin/courses', icon: BookOpenIcon, permission: 'courses' },
            {
                name: 'Enquiries',
                href: '/admin/enquiries',
                icon: UserGroupIcon,
                permission: 'enquiries'
            },
            {
                name: 'Contacts',
                href: '/admin/enquiries/contacts',
                icon: ChatBubbleLeftRightIcon,
                permission: 'enquiries'
            },
            { name: 'Banners', href: '/admin/banners', icon: PhotoIcon, permission: 'banners' },
            { name: 'Blogs', href: '/admin/blogs', icon: PencilSquareIcon, permission: 'blogs' },
            { name: 'FAQs', href: '/admin/faqs', icon: QuestionMarkCircleIcon, permission: 'faqs' }
        ]

        return allNavigation.filter(item => {
            if (item.adminOnly && user?.role !== 'admin') return false
            return !item.permission || hasPermission(item.permission)
        })
    }

    const getFilteredMasterDataNavigation = () => {
        const allMasterDataNavigation = [
            { name: 'Approvals', href: '/admin/approvals', icon: CheckBadgeIcon, permission: 'universities' },
            { name: 'Specializations', href: '/admin/specializations', icon: CogIcon, permission: 'courses' },
            { name: 'Degrees', href: '/admin/degrees', icon: DocumentTextIcon, permission: 'courses' },
            { name: 'Admin Users', href: '/admin/users', icon: UserIcon, permission: 'admin-users', adminOnly: true },
        ]

        return allMasterDataNavigation.filter(item => {
            if (item.adminOnly && user?.role !== 'admin') return false
            return !item.permission || hasPermission(item.permission)
        })
    }

    const navigation = getFilteredNavigation()
    const masterDataNavigation = getFilteredMasterDataNavigation()

    const handleLogout = () => {
        logout()
        navigate('/admin/login')
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Mobile sidebar */}
            <AnimatePresence>
                {sidebarOpen && (
                    <>
                        <div className="fixed inset-0 z-40 lg:hidden">
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
                        </div>
                        <motion.div
                            initial={{ x: -300 }}
                            animate={{ x: 0 }}
                            exit={{ x: -300 }}
                            transition={{ duration: 0.3 }}
                            className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl border-r border-gray-200 lg:hidden flex flex-col"
                        >
                            {/* Header */}
                            <div className="flex h-16 items-center justify-between px-6 border-b border-gray-200 flex-shrink-0">
                                <div className="flex items-center space-x-3">
                                    <img
                                        src="/marglogo.png"
                                        alt="Margdarshak Logo"
                                        className=" h-12 object-contain border-2 border-white rounded-lg"
                                    />
                                </div>
                                <button
                                    onClick={() => setSidebarOpen(false)}
                                    className="p-2 rounded-md text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Scrollable Navigation */}
                            <div className="flex-1 overflow-y-auto py-4">
                                <nav className="px-4 space-y-1">
                                    {navigation.map((item) => {
                                        const isActive = location.pathname === item.href || (item.children && item.children.some(child => location.pathname === child.href))
                                        return (
                                            <div key={item.name}>
                                                <Link
                                                    to={item.href}
                                                    onClick={() => setSidebarOpen(false)}
                                                    className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl mb-2 transition-all duration-200 ${isActive
                                                        ? 'bg-accent-100 text-accent-700 shadow-sm'
                                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                        }`}
                                                >
                                                    <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-accent-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                                                    {item.name}
                                                </Link>
                                                {item.children && (
                                                    <div className="ml-8 space-y-1">
                                                        {item.children.map((child) => {
                                                            const isChildActive = location.pathname === child.href
                                                            return (
                                                                <Link
                                                                    key={child.name}
                                                                    to={child.href}
                                                                    onClick={() => setSidebarOpen(false)}
                                                                    className={`block px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${isChildActive
                                                                        ? 'bg-primary-100 text-primary-700'
                                                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                                                        }`}
                                                                >
                                                                    {child.name}
                                                                </Link>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}

                                    {/* Master Data Section */}
                                    {masterDataNavigation.length > 0 && (
                                        <div className="mt-6">
                                            <h3 className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 pt-4 border-t border-gray-200">
                                                Master Data
                                            </h3>
                                            {masterDataNavigation.map((item) => {
                                                const isActive = location.pathname === item.href
                                                return (
                                                    <Link
                                                        key={item.name}
                                                        to={item.href}
                                                        onClick={() => setSidebarOpen(false)}
                                                        className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl mb-2 transition-all duration-200 ${isActive
                                                            ? 'bg-accent-100 text-accent-700 shadow-sm'
                                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                            }`}
                                                    >
                                                        <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-accent-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                                                        {item.name}
                                                    </Link>
                                                )
                                            })}
                                        </div>
                                    )}
                                </nav>
                            </div>

                            {/* User Info and Logout Button at Bottom */}
                            <div className="border-t border-gray-200 p-4 flex-shrink-0">
                                <div className="flex items-center space-x-3 mb-3">
                                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                        <UserIcon className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'Admin User'}</p>
                                        <p className="text-xs text-gray-500">{getUserRoleDisplay()}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                                >
                                    <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                                    Logout
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Desktop sidebar */}
            <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:w-64 lg:bg-white lg:shadow-xl lg:border-r lg:border-gray-200 lg:flex lg:flex-col">
                {/* Header */}
                <div className="flex h-16 items-center px-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <img
                            src="/marglogo.png"
                            alt="Margdarshak Logo"
                            className=" h-12 object-contain border-2 border-white rounded-lg"
                        />
                    </div>
                </div>

                {/* Scrollable Navigation */}
                <div className="flex-1 overflow-y-auto py-4">
                    <nav className="px-4 space-y-1">
                        {navigation.map((item) => {
                            const isActive = location.pathname === item.href || (item.children && item.children.some(child => location.pathname === child.href))
                            return (
                                <div key={item.name}>
                                    <Link
                                        to={item.href}
                                        className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl mb-2 transition-all duration-200 ${isActive
                                            ? 'bg-accent-100 text-accent-700 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                            }`}
                                    >
                                        <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-accent-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                                        {item.name}
                                    </Link>
                                    {item.children && (
                                        <div className="ml-8 space-y-1">
                                            {item.children.map((child) => {
                                                const isChildActive = location.pathname === child.href
                                                return (
                                                    <Link
                                                        key={child.name}
                                                        to={child.href}
                                                        className={`block px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${isChildActive
                                                            ? 'bg-primary-100 text-primary-700'
                                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {child.name}
                                                    </Link>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )
                        })}

                        {/* Master Data Section */}
                        {masterDataNavigation.length > 0 && (
                            <div className="mt-6">
                                <h3 className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 pt-4 border-t border-gray-200">
                                    Master Data
                                </h3>
                                {masterDataNavigation.map((item) => {
                                    const isActive = location.pathname === item.href
                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl mb-2 transition-all duration-200 ${isActive
                                                ? 'bg-accent-100 text-accent-700 shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                }`}
                                        >
                                            <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-accent-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                                            {item.name}
                                        </Link>
                                    )
                                })}
                            </div>
                        )}
                    </nav>
                </div>

                {/* User Info and Logout Button at Bottom */}
                <div className="border-t border-gray-200 p-4 flex-shrink-0">
                    <div className="flex items-center space-x-3 mb-3">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-primary-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'Admin User'}</p>
                            <p className="text-xs text-gray-500">{getUserRoleDisplay()}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                    >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                        Logout
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="lg:pl-64">
                {/* Header */}
                <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
                    <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center">
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="p-2 rounded-md text-gray-400 hover:text-gray-600 lg:hidden"
                            >
                                <Bars3Icon className="w-6 h-6" />
                            </button>
                            <div className="ml-4 lg:ml-0">
                                <h1 className="text-xl font-semibold text-gray-900">
                                    {navigation.find(item => item.href === location.pathname)?.name || 'Admin Panel'}
                                </h1>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                    <UserIcon className="w-5 h-5 text-primary-600" />
                                </div>
                                <div className="hidden sm:block">
                                    <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin User'}</p>
                                    <p className="text-xs text-gray-500">{getUserRoleDisplay()}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Page content */}
                <main className="flex-1">
                    <Outlet />
                </main>
            </div>
        </div>
    )
}

export default AdminLayout
